import SegmentChip from 'components/@extended/SegmentChip.tsx';
import StatusChip from 'components/@extended/StatusChip';
import PageHeader from 'components/PageHeader';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { useContext } from 'react';
import { MetaItem, RecordHeader, RecordMoreMenu } from 'components/record-header/RecordHeader';
import { getLinks } from 'pages/matters/util';
import { FormattedMessage } from 'react-intl';
import {
    useDeletePartyGroupEmployeeHistoryEntryMutation,
    useGetPartyGroupEmployeeHistoryQuery,
    useUpdatePartyGroupEmployeeHistoryEntryMutation,
} from 'store/api/groups.ts';
import { ClassifierData } from 'types/classifiers';
import { GroupEmployeeRoleType, PartyGroup } from 'types/groups';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useGoBack, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import GroupIcon from './GroupIcon';
import { useDeleteGroup } from './useDeleteGroup';

interface Props {
    partyGroup: PartyGroup;
    classifiers: Record<string, ClassifierData>;
    /** The new design: the case's header, without Edit (the cards edit in place) and with Delete in its ⋯ menu. */
    isNew?: boolean;
}

const DELETE_PERMISSIONS = ['DELETE.PARTY_GROUPS.ALL'];

export const GroupPageHeader = ({ partyGroup, classifiers, isNew = false }: Props): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const canDelete = useHasPermissions(DELETE_PERMISSIONS);
    const deleteGroup = useDeleteGroup(String(partyGroup.id));
    const goBack = useGoBack('/groups');
    // A draft's header: the Draft chip, who created it and when it was saved, its own actions and its notice.
    const draftActions = useContext(DraftActionsContext);
    const draft = draftActions?.draft;
    const title = partyGroup.groupName || (draftActions ? t('new-entity.group') : '-');
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;
    const partners = partyGroup.employees
        ?.filter(
            (partyGroupEmployee) =>
                partyGroupEmployee.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_PARTNER
        )
        .map(({ employee }) => employee);
    const managers = partyGroup.employees
        ?.filter(
            (partyGroupEmployee) =>
                partyGroupEmployee.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_MANAGER
        )
        .map(({ employee }) => employee);

    // A draft has no history of its own yet.
    const historyButton = (fieldType: HistoryFieldType, fieldNameId: string) =>
        draftActions ? null : (
            <DisplayHistoryButton
                entityType={HistoryEntityType.PARTY_GROUP}
                entityId={`${partyGroup.id}`}
                fieldType={fieldType}
                fieldName={t(fieldNameId)}
                activity={''}
                query={useGetPartyGroupEmployeeHistoryQuery}
                updateMutation={useUpdatePartyGroupEmployeeHistoryEntryMutation}
                deleteMutation={useDeletePartyGroupEmployeeHistoryEntryMutation}
            />
        );

    if (isNew) {
        const groupType = partyGroup.groupTypeCode
            ? getClassifierText(classifiers['GROUP_TYPE'], partyGroup.groupTypeCode)
            : '';
        const header = (
            <RecordHeader
                chips={
                    <>
                        {draftActions ? <DraftChip /> : null}
                        {groupType ? <StatusChip type="neutral" label={groupType} /> : null}
                        {partyGroup.segmentTypeCode != null ? (
                            <SegmentChip segmentTypeCode={partyGroup.segmentTypeCode} />
                        ) : null}
                    </>
                }
                title={title}
                pin={draftActions ? undefined : { kind: 'group', id: partyGroup.id }}
                titleNode={
                    <>
                        {title}
                        <GroupIcon size="large" />
                    </>
                }
                meta={
                    <>
                        {partners?.length ? (
                            <MetaItem label={<FormattedMessage id="contacts.relationship-partners" />}>
                                <span>{getLinks('employee', partners)}</span>
                                {historyButton(HistoryFieldType.RELATIONSHIP_PARTNER, 'contacts.relationship-partners')}
                            </MetaItem>
                        ) : null}
                        {managers?.length ? (
                            <MetaItem label={<FormattedMessage id="contacts.relationship-managers" />}>
                                <span>{getLinks('employee', managers)}</span>
                                {historyButton(HistoryFieldType.RELATIONSHIP_MANAGER, 'contacts.relationship-managers')}
                            </MetaItem>
                        ) : null}
                        {partyGroup.serviceLevelTypeCode != null ? (
                            <MetaItem label={<FormattedMessage id="contacts.service-level-type" />}>
                                {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], partyGroup.serviceLevelTypeCode)}
                            </MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={
                    draftActions ? (
                        <DraftRecordActions actions={draftActions} />
                    ) : (
                        <RecordMoreMenu
                            menuItems={
                                canDelete ? [{ title: t('delete'), variant: 'destructive', handler: deleteGroup }] : []
                            }
                        />
                    )
                }
            />
        );
        return draftNotice ? (
            <div className="flex flex-col gap-4">
                {header}
                {draftNotice}
            </div>
        ) : (
            header
        );
    }

    return (
        <>
            <PageHeader
                title={title}
                showBreadcrumb
                onBack={goBack}
                iconsEnd={<GroupIcon size="large" />}
                buttons={
                    draftActions ? (
                        <div className="flex items-center justify-end gap-2">
                            <DraftRecordActions actions={draftActions} />
                        </div>
                    ) : (
                        <div className="flex justify-end gap-2">
                            <PermissionButton
                                permissions={['UPDATE.PARTY_GROUPS.ALL', 'UPDATE.PARTY_GROUPS.MY_OWN']}
                                variant="outlined"
                                size="small"
                                onClick={() => navigateWithReturn(`/group/${partyGroup.id}/edit`)}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    )
                }
            >
                <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                    <div className="flex gap-1">
                        {draftActions ? <DraftChip /> : null}
                        {partyGroup.segmentTypeCode != null && (
                            <SegmentChip segmentTypeCode={partyGroup.segmentTypeCode} />
                        )}
                        {partyGroup.serviceLevelTypeCode != null && (
                            <span className="text-subtitle2 text-grey-600 flex items-center font-semibold">
                                {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], partyGroup.serviceLevelTypeCode)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-[12px] leading-5 font-medium">
                        {partners?.length ? (
                            <div className="flex items-center gap-1">
                                <FormattedMessage id="contacts.relationship-partners" />:
                                <div>{getLinks('employee', partners)}</div>
                                {historyButton(HistoryFieldType.RELATIONSHIP_PARTNER, 'contacts.relationship-partners')}
                            </div>
                        ) : null}
                        {managers?.length ? (
                            <div className="flex items-center gap-1">
                                <FormattedMessage id="contacts.relationship-managers" />:
                                <div>{getLinks('employee', managers)}</div>
                                {historyButton(HistoryFieldType.RELATIONSHIP_MANAGER, 'contacts.relationship-managers')}
                            </div>
                        ) : null}
                    </div>
                    {draft ? <DraftMeta draft={draft} /> : null}
                </div>
            </PageHeader>
            {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
        </>
    );
};
